<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4">
    <h3>Åpningstider</h3>
    <div id="updatedMessage" v-if="openingHoursUpdated">
      <span class="message-info">Åpningstidene er oppdatert</span>
    </div>
    <br/>
    <span v-if="useFunctionality !== 'WEBSITE'">
      Online bestillinger i nettbutikk skal hentes eller leveres innenfor åpningstidene.
    </span>

    <div v-if="hasMultiLocations()" class="select-location-box mt-4">
      <span class="ingredient-title">For avdeling</span>
      <select class="form-select" v-model="locationId" @change="getOpeningHours()">
        <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
      </select>
    </div>

    <div class="opening-hours-flex-box">
      <div v-for="day in days" :key="day.key" class="opening-hours">
        <div>
          <span class="item-title">{{ day.label }}</span>
          <select class="openOrClose" v-model="dayData[day.key].status">
            <option value="open">Åpen</option>
            <option value="close">Stengt</option>
          </select>
        </div>
        <div class="mt-3">
          <span class="time-label">Fra: </span>
          <TimePicker
            v-model="dayData[day.key].fromTime"
            :disabled="dayData[day.key].status === 'close'"
            :step-minutes="15"
            :default-hour="11"
            :default-minute="0"
          />
        </div>
        <div class="mt-3">
          <span class="time-label">Til: </span>
          <TimePicker
            v-model="dayData[day.key].toTime"
            :disabled="dayData[day.key].status === 'close'"
            :step-minutes="15"
            :default-hour="22"
            :default-minute="0"
          />
        </div>
        <div class="mt-3">
          Kjøkken stenger kl. <TimePicker
            v-model="dayData[day.key].kitchenClosingTime"
            :disabled="dayData[day.key].status === 'close'"
            :step-minutes="15"
            :default-hour="21"
            :default-minute="30"
          />
        </div>
      </div>
    </div>

    <div class="bo_savebtn_container">
      <hr/>
      <button type="button" class="bo_btn" @click="save"> Lagre </button>
    </div>
  </div>
</template>

<script>
import SettingsService from "@/services/settings/SettingsService";
import LoadingSpinner from "@/components/other/LoadingSpinner";
import TimePicker from "@/components/other/TimePicker";
import MerchantUtil from "@/util/MerchantUtil";
import LocationUtil from "@/util/LocationUtil";
import LocationService from "@/services/location/LocationService";

export default {
  components: { LoadingSpinner, TimePicker },
  data() {
    return {
      loading: true,
      locationId: '',
      locations: [],
      openingHoursDocument: {},
      openingHoursUpdated: false,
      days: [
        { key: 'monday', label: 'Mandag', dayEnum: 'MONDAY', weekday: 1 },
        { key: 'tuesday', label: 'Tirsdag', dayEnum: 'TUESDAY', weekday: 2 },
        { key: 'wednesday', label: 'Onsdag', dayEnum: 'WEDNESDAY', weekday: 3 },
        { key: 'thursday', label: 'Torsdag', dayEnum: 'THURSDAY', weekday: 4 },
        { key: 'friday', label: 'Fredag', dayEnum: 'FRIDAY', weekday: 5 },
        { key: 'saturday', label: 'Lørdag', dayEnum: 'SATURDAY', weekday: 6 },
        { key: 'sunday', label: 'Søndag', dayEnum: 'SUNDAY', weekday: 7 }
      ],
      dayData: {
        monday: { status: 'close', fromTime: '', toTime: '', kitchenClosingTime: '' },
        tuesday: { status: 'close', fromTime: '', toTime: '', kitchenClosingTime: '' },
        wednesday: { status: 'close', fromTime: '', toTime: '', kitchenClosingTime: '' },
        thursday: { status: 'close', fromTime: '', toTime: '', kitchenClosingTime: '' },
        friday: { status: 'close', fromTime: '', toTime: '', kitchenClosingTime: '' },
        saturday: { status: 'close', fromTime: '', toTime: '', kitchenClosingTime: '' },
        sunday: { status: 'close', fromTime: '', toTime: '', kitchenClosingTime: '' }
      }
    };
  },
  computed: {
    useFunctionality() {
      return MerchantUtil.useFunctionality();
    }
  },
  mounted() {
    LocationService.listLocations().then(
      (response) => {
        this.locations = response.data;
        this.locationId = this.locations.length ? this.locations[0].id : '';
        this.getOpeningHours();
      },
      (error) => {
        console.log(error);
      }
    );
  },
  methods: {
    hasMultiLocations() {
      return LocationUtil.hasMultiLocations();
    },
    getOpeningHours() {
      this.loading = true;
      SettingsService.getOpeningHours(this.locationId).then(
        (response) => {
          if (response.data) {
            this.openingHoursDocument = response.data;
          }
          this.initData();
          this.loading = false;
        },
        (error) => {
          console.log(error);
        }
      );
    },
    initData() {
      // Reset all days to closed
      for (const day of this.days) {
        this.dayData[day.key] = {
          status: 'close',
          fromTime: '',
          toTime: '',
          kitchenClosingTime: ''
        };
      }

      if (this.openingHoursDocument && this.openingHoursDocument.openingHours) {
        const openingHours = this.openingHoursDocument.openingHours;

        for (const day of this.days) {
          const dayHours = this.getDayOpenHours(day.dayEnum, openingHours);
          if (dayHours != null) {
            this.dayData[day.key] = {
              status: 'open',
              fromTime: dayHours.fromTime || '',
              toTime: dayHours.toTime || '',
              kitchenClosingTime: dayHours.kitchenClosingTime || ''
            };
          }
        }
      }
    },
    getDayOpenHours(day, openingHours) {
      if (openingHours) {
        for (let i = 0; i < openingHours.length; i++) {
          if (day === openingHours[i].day) {
            return openingHours[i];
          }
        }
      }
      return null;
    },
    getOpeningHoursInputs() {
      const openingHours = [];

      for (const day of this.days) {
        if (this.dayData[day.key].status === 'open') {
          openingHours.push({
            day: day.dayEnum,
            weekday: day.weekday,
            fromTime: this.dayData[day.key].fromTime,
            toTime: this.dayData[day.key].toTime,
            kitchenClosingTime: this.dayData[day.key].kitchenClosingTime
          });
        }
      }

      return openingHours;
    },
    save() {
      this.loading = true;

      const openingHours = this.getOpeningHoursInputs();
      if (JSON.stringify(this.openingHoursDocument) === '{}') {
        // Blank object, CREATE
        this.openingHoursDocument = {
          type: "openinghours",
          merchant: MerchantUtil.getMerchant(),
          locationId: this.locationId,
          openingHours: openingHours
        };
      } else {
        // UPDATE
        this.openingHoursDocument.openingHours = openingHours;
      }

      SettingsService.editOpeningHours(this.openingHoursDocument).then(
        (response) => {
          const documentId = response.data;
          if (documentId) {
            this.openingHoursUpdated = true;
          } else {
            console.log("Editing openingHours failed");
          }
          this.loading = false;
        },
        (error) => {
          console.log(error);
        }
      );
    }
  }
};
</script>
